import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, fetchUser } from "@/modules/auth/store/authSlice";
import { useTranslation } from "react-i18next";
import CompanyAvatar from "./CompanyAvatar";
import { AccountMenuDivider, AccountMenuHeader, AccountMenuItems } from "./AccountMenu";
import NotificationsMenu from "./NotificationsMenu";
import SideDrawer, { DrawerToggleButton } from "./SideDrawer";
import ServicesNavList from "./ServicesNavList";
import { BellIcon, BookOpen, ChevronDown, Coins, Headphones, Layers, MoreVertical, UserRound, Users, X } from "lucide-react";
import { isNonSaudiApplicantUser } from "@/modules/registration/utils/nonSaudiApplicant";
import {
  selectUnreadByUser,
  markAsRead,
} from "@/modules/notifications/store/notificationsSlice";
import ThemeSwitcher from "./ThemeSwitcher";
import LanguageSwitcher from "./LanguageSwitcher";
import NavMenu from "./NavMenu";
import { getServiceModuleName, SERVICE_MODULES } from "../config/serviceModules";
import { buildNavbarMenusByKeyFromManifests } from "@/modules/manifestRegistry";
import { fetchWallet } from "@/modules/wallet/api/wallet";
import { useFeature } from "@/modules/shared/store/featuresSlice";

// Rows in the mobile drawer's link list; "sm" is for nested child links.
const drawerRowClass = (active, size = "md") =>
  `flex w-full items-center gap-2 rounded-lg px-3 text-start transition-colors duration-150 ${
    size === "sm" ? "py-2.5 text-sm" : "py-3 text-[15px]"
  } ${
    active
      ? "bg-emerald-50 font-semibold text-(--primary-green) dark:bg-emerald-500/10 dark:text-emerald-400"
      : "font-medium text-slate-700 hover:bg-slate-100 active:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700 dark:active:bg-slate-700"
  }`;
const drawerDisabledClass = (size = "md") =>
  `flex w-full cursor-not-allowed items-center gap-2 rounded-lg px-3 font-medium text-slate-400 dark:text-slate-500 ${
    size === "sm" ? "py-2.5 text-sm" : "py-3 text-[15px]"
  }`;
const SOON_BADGE_CLASS =
  "ms-auto rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-700 dark:text-slate-400";

export default function Navbar() {
  const { i18n, t } = useTranslation();
  const user = useSelector((s) => s?.auth?.user);
  const isApplicant = isNonSaudiApplicantUser(user);
  const isApprovedApplicant =
    isApplicant &&
    String(user?.registration_status || "").toLowerCase() === "approved";
  const isRestrictedApplicant = isApplicant && !isApprovedApplicant;

  // Public website links are only enabled when explicitly pointed at the production site.
  // (Vite only exposes env vars prefixed with VITE_ by default.)
  const publicSiteUrl = (() => {
    const raw =
      import.meta.env?.VITE_PUBLIC_SITE_URL ||
      import.meta.env?.VITE_Public_site_URL ||
      import.meta.env?.PUBLIC_SITE_URL ||
      import.meta.env?.Public_site_URL ||
      "";
    return String(raw || "")
      .trim()
      .replace(/\/+$/, "");
  })();
  const publicLinksEnabled = publicSiteUrl === "https://tarhplatform.com";
  const publicLink = (path) => (publicLinksEnabled ? `${publicSiteUrl}${path}` : "#");

  const otpPending = (() => {
    try {
      return localStorage.getItem("otp_pending") === "1";
    } catch (_) {
      return false;
    }
  })();
  const otpPendingActive = otpPending && !isApplicant;
  const dispatch = useDispatch();
  const location = useLocation();
  const subscribed = useSelector((s) => s.subscriptions?.subscribed);
  const navigate = useNavigate();
  const currentLang = i18n.language;
  const isRtl = currentLang === "ar";
  const allowedModuleKeys = useSelector((s) => s.subscriptions?.allowedModules || []);
  const activeModule = React.useMemo(() => {
    if (!user) return null;
    const matches = SERVICE_MODULES.filter((module) =>
      module.matchers.some((matcher) => matcher.test(location.pathname))
    );
    if (matches.length === 0) return null;
    const requestedModule = matches.find(
      (module) => module.key === location.state?.activeServiceModule
    );
    if (requestedModule && allowedModuleKeys.includes(requestedModule.key)) {
      return requestedModule;
    }
    // Shared pages such as All Tenders belong to the module the user is subscribed to.
    return matches.find((module) => allowedModuleKeys.includes(module.key)) || matches[0];
  }, [location.pathname, location.state?.activeServiceModule, user, allowedModuleKeys]);
  // const notifications = useSelector((s) => s?.notifications?.items);
  const unread = useSelector((s) => selectUnreadByUser(s, user?.uuid));
  const documentVerified = user?.document_verified !== false;
  const branchesEnabled = useFeature("branches");
  const branchAssignments = Array.isArray(user?.branch_assignments)
    ? user.branch_assignments
    : [];
  const showBranchSwitcher = branchesEnabled && branchAssignments.length > 1;
  const [activeBranchId, setActiveBranchId] = useState("");

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesMenuOpen, setIsServicesMenuOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [nfDropdownOpen, setNfDropdownOpen] = useState(false);
  const [openMenuKey, setOpenMenuKey] = useState(null);
  const [mobileOpenMenuKey, setMobileOpenMenuKey] = useState(null);
  const dropdownRef = useRef(null);
  const menuCloseTimerRef = useRef(null);
  const desktopMenuViewportRef = useRef(null);
  const desktopMenuMeasureItemRefs = useRef([]);
  const desktopMenuMeasureMoreRef = useRef(null);
  const [desktopVisibleMenuCount, setDesktopVisibleMenuCount] = useState(0);
  const [walletPoints, setWalletPoints] = useState(null);

  const unreadCount = documentVerified ? unread?.length || 0 : 0;
  const walletCompanyId = user?.company_id || user?.company?.id;
  // Latin digits in Arabic too, matching the rest of the app.
  const formattedWalletPoints =
    walletPoints === null
      ? "—"
      : new Intl.NumberFormat(currentLang === "ar" ? "ar-u-nu-latn" : currentLang).format(
          Number(walletPoints) || 0
        );
  const walletPointsLabel = t("wallet_points_balance", { count: formattedWalletPoints });

  useEffect(() => {
    let active = true;
    if (!walletCompanyId) {
      setWalletPoints(null);
      return () => {
        active = false;
      };
    }

    const refreshBalance = async () => {
      try {
        const wallet = await fetchWallet();
        if (active) setWalletPoints(wallet?.available_points ?? 0);
      } catch {
        if (active) setWalletPoints(null);
      }
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshBalance();
    };

    refreshBalance();
    window.addEventListener("focus", refreshBalance);
    window.addEventListener("wallet:changed", refreshBalance);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.removeEventListener("focus", refreshBalance);
      window.removeEventListener("wallet:changed", refreshBalance);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [user?.id, walletCompanyId]);

  useEffect(() => {
    if (!walletCompanyId || !/^\/(profile|wallet\/recharge)(\/|$)/.test(location.pathname)) return;
    let active = true;
    fetchWallet()
      .then((wallet) => {
        if (active) setWalletPoints(wallet?.available_points ?? 0);
      })
      .catch(() => {
        if (active) setWalletPoints(null);
      });
    return () => {
      active = false;
    };
  }, [location.pathname, walletCompanyId]);

  useEffect(() => {
    if (!branchesEnabled || branchAssignments.length === 0) {
      localStorage.removeItem("active_branch_id");
      setActiveBranchId("");
      return;
    }
    const stored = localStorage.getItem("active_branch_id");
    const selected = branchAssignments.find((assignment) =>
      String(assignment.branch_public_id || assignment.branch_id) === String(stored)
    );
    const fallback = branchAssignments.find((assignment) => assignment.is_default) || branchAssignments[0];
    const next = selected || fallback;
    const nextId = String(next.branch_public_id || next.branch_id);
    localStorage.setItem("active_branch_id", nextId);
    setActiveBranchId(nextId);
  }, [branchesEnabled, user?.id, user?.branch_assignments]);

  const handleBranchChange = async (event) => {
    const nextId = event.target.value;
    localStorage.setItem("active_branch_id", nextId);
    setActiveBranchId(nextId);
    await dispatch(fetchUser());
    navigate("/dashboard");
  };

  const handlemarkAsRead = async (id) => {
    dispatch(markAsRead({ id, userUuid: user.uuid }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".notification-wrapper")) {
        setNfDropdownOpen((prev) => (prev ? false : prev));
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setNfDropdownOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".menu-dropdown-wrapper")) {
        setOpenMenuKey((prev) => (prev === null ? prev : null));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setOpenMenuKey((prev) => (prev === null ? prev : null));
      setIsDropdownOpen((prev) => (prev ? false : prev));
      setNfDropdownOpen((prev) => (prev ? false : prev));
      setIsMobileDropdownOpen((prev) => (prev ? false : prev));
      setMobileOpenMenuKey((prev) => (prev === null ? prev : null));
      setIsMobileMenuOpen((prev) => (prev ? false : prev));
      setIsServicesMenuOpen((prev) => (prev ? false : prev));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const openMenu = (key) => {
    if (menuCloseTimerRef.current) {
      clearTimeout(menuCloseTimerRef.current);
      menuCloseTimerRef.current = null;
    }
    setOpenMenuKey(key);
  };

  const toggleMenu = (key) => {
    if (menuCloseTimerRef.current) {
      clearTimeout(menuCloseTimerRef.current);
      menuCloseTimerRef.current = null;
    }
    setOpenMenuKey((prev) => (prev === key ? null : key));
  };

  const closeMenuSoon = () => {
    if (menuCloseTimerRef.current) {
      clearTimeout(menuCloseTimerRef.current);
    }
    menuCloseTimerRef.current = setTimeout(() => {
      setOpenMenuKey(null);
      menuCloseTimerRef.current = null;
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (menuCloseTimerRef.current) {
        clearTimeout(menuCloseTimerRef.current);
        menuCloseTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setOpenMenuKey((prev) => (prev === null ? prev : null));
  }, [location.pathname]);

  // Defensive: when language changes, force-close any open dropdown state so we
  // don't end up with mixed-language menu snapshots during fast toggles.
  useEffect(() => {
    setOpenMenuKey((prev) => (prev === null ? prev : null));
    setMobileOpenMenuKey((prev) => (prev === null ? prev : null));
    setIsDropdownOpen((prev) => (prev ? false : prev));
    setIsMobileDropdownOpen((prev) => (prev ? false : prev));
    setNfDropdownOpen((prev) => (prev ? false : prev));
  }, [currentLang]);

  const navItems = React.useMemo(
    () => [
      { label: t("aboutTarh"), path: publicLink("/") },
      {
        label: t("electronicServices"),
        path: publicLink("/services"),
      },
      {
        label: t("guidanceLibrary"),
        path: publicLink("/helpcenter"),
      },
      { label: t("contactUs"), path: publicLink("/contact") },
    ],
    [publicLinksEnabled, publicSiteUrl, t]
  );

  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;
  const ALL_TENDERS_ENABLED = true;
  const COMPANY_LIST_ENABLED = false;
  const isAuthorizedOnly =
    !!user &&
    (user.role === "authorized_user" ||
      user.auth_context === "authorized_user");

  const hasPermission = (permissions = [], mode = "any") => {
    if (!isAuthorizedOnly) return true;
    const list = Array.isArray(permissions)
      ? permissions.filter(Boolean)
      : permissions
        ? [permissions]
        : [];
    if (list.length === 0) return true;
    const check = (key) => !!user?.[`can_${key}`];
    return mode === "all" ? list.every(check) : list.some(check);
  };

  const applyMenuPermissions = (menu) => {
    const permissionList = menu.permissions || [];
    const permissionMode = menu.permissionMode || "any";
    const permissionBlocked =
      permissionList.length > 0 && !hasPermission(permissionList, permissionMode);
    const disabled = !!menu.disabled || permissionBlocked;
    const disabledReason =
      menu.disabledReason || (permissionBlocked ? "permission" : undefined);
    const children = Array.isArray(menu.children)
      ? menu.children.map(applyMenuPermissions)
      : menu.children;
    return { ...menu, disabled, disabledReason, children };
  };

  const activeModuleLabel =
    activeModule?.key === "offers_management" ||
      activeModule?.key === "tenders_offers_management" || activeModule?.key === "financial_claims_management" || activeModule?.key ===
      "approvals_requests_management" || activeModule?.key === "proposal_approvals_management" || activeModule?.key === "delegation_permissions_management" || activeModule?.key === "negotiation_award_management"
      ? t("dashboard")
      : activeModule
        ? getServiceModuleName(activeModule, currentLang, "dashboard", t)
        : "";


  const moduleMenusByKey = React.useMemo(
    () =>
      buildNavbarMenusByKeyFromManifests({
        t,
        activeModuleLabel,
        ALL_TENDERS_ENABLED,
        COMPANY_LIST_ENABLED,
        isAuthorizedOnly,
        user,
      }),
    [ALL_TENDERS_ENABLED, COMPANY_LIST_ENABLED, activeModuleLabel, isAuthorizedOnly, t, user]
  );

  const expandedActiveModuleMenus = React.useMemo(() => {
    if (!activeModule) {
      return [];
    }

    const moduleMenus = moduleMenusByKey[activeModule.key] || [];
    const hasDeclaredDefaultPath = moduleMenus.some((item) => {
      if (item?.path === activeModule.defaultPath) return true;
      if (!Array.isArray(item?.children)) return false;
      return item.children.some((child) => child?.path === activeModule.defaultPath);
    });

    const flattened = hasDeclaredDefaultPath
      ? []
      : [
        {
          label: activeModuleLabel,
          path: activeModule.defaultPath,
        },
      ];

    moduleMenus.forEach((item) => {
      if (Array.isArray(item.children) && item.children.length > 0) {
        flattened.push(...item.children);
        return;
      }

      flattened.push(item);
    });

    const seen = new Set();
    return flattened.filter((item) => {
      const key = item.path;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [activeModule, activeModuleLabel, moduleMenusByKey]);

  const authenticatedMenus = React.useMemo(() => {
    if (!user || otpPendingActive || isRestrictedApplicant) {
      return navItems;
    }
    if (!activeModule) {
      return navItems;
    }
    // Switching services now happens in the services menu (hamburger), so no "Main Menu" link here.
    return expandedActiveModuleMenus;
  }, [activeModule, expandedActiveModuleMenus, isRestrictedApplicant, navItems, otpPendingActive, user]);

  const permissionAwareMenus = React.useMemo(
    () => authenticatedMenus.map(applyMenuPermissions),
    [authenticatedMenus]
  );

  const filteredMenus = React.useMemo(
    () =>
      permissionAwareMenus.filter((menu) => {
        if (location.pathname === "/launch") return false;
        if (menu.path === "/notifications" && isDesktop && !activeModule)
          return false;
        if (menu.path === "/notifications" && user && !documentVerified)
          return false;

        return true;
      }),
    [activeModule, documentVerified, isDesktop, location.pathname, permissionAwareMenus, user]
  );

  // Guardrail: ensure we never render a mixed public+module navbar row.
  // Public navbar items are external URLs/#, module navbar items are internal routes (/...).
  const isModuleNavbarMode = !!user && !otpPendingActive && !isRestrictedApplicant && !!activeModule;
  const navbarMenus = React.useMemo(() => {
    const scopedMenus = isModuleNavbarMode
      ? filteredMenus.filter(
        (menu) =>
          (typeof menu?.path === "string" && menu.path.startsWith("/")) ||
          (Array.isArray(menu?.children) && menu.children.length > 0)
      )
      : filteredMenus.filter(
        (menu) => typeof menu?.path === "string" && !menu.path.startsWith("/")
      );

    const seen = new Set();
    return scopedMenus.filter((menu) => {
      const key = `${menu.path}::${menu.label || ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [filteredMenus, isModuleNavbarMode]);

  // Longest matching path wins, so detail pages highlight their parent link.
  const activeMenuPath = React.useMemo(() => {
    const current = `${location.pathname}${location.search}`;
    let best = null;
    let bestScore = 0;
    const visit = (items) =>
      items.forEach((item) => {
        if (Array.isArray(item?.children)) visit(item.children);
        if (typeof item?.path !== "string" || !item.path.startsWith("/")) return;
        const base = item.path.split("?")[0];
        const matches =
          location.pathname === base ||
          (!item.exact && location.pathname.startsWith(`${base}/`));
        if (!matches) return;
        const score = base.length + (item.path === current ? 0.5 : 0);
        if (score > bestScore) {
          best = item.path;
          bestScore = score;
        }
      });
    visit(navbarMenus);
    return best;
  }, [location.pathname, location.search, navbarMenus]);

  const isMenuActive = (item) =>
    item?.path === activeMenuPath ||
    (Array.isArray(item?.children) && item.children.some(isMenuActive));

  const withLinkState = (item) => ({
    ...item,
    state: item.path === "/all-tenders" ? { activeServiceModule: activeModule?.key } : undefined,
  });

  // The link row eases in when navigating to another module. It stays still on the first
  // render, on a language switch, and when the module resolves late on the same page.
  const navModuleKey = isModuleNavbarMode ? activeModule.key : "public";
  const [navSwap, setNavSwap] = useState({
    key: navModuleKey,
    lang: currentLang,
    path: location.pathname,
    animate: false,
  });
  if (navSwap.key !== navModuleKey || navSwap.lang !== currentLang) {
    setNavSwap({
      key: navModuleKey,
      lang: currentLang,
      path: location.pathname,
      animate: navSwap.key !== navModuleKey && navSwap.path !== location.pathname,
    });
  }
  const navAnnouncement =
    navSwap.animate && isModuleNavbarMode
      ? getServiceModuleName(activeModule, currentLang, "dashboard", t)
      : "";

  const accountMenu = [
    // { label: t("dashboard"), path: "/dashboard" },

    { label: t("notifications"), path: "/notifications", icon: BellIcon, badge: unreadCount },
    { label: t("profile"), path: "/profile", icon: UserRound },
    {
      label: t("authorized_users"),
      path: "/delegates/permissions",
      icon: Users,
    },
    {
      label: t("field_of_work_classification_title"),
      path: "/field-of-work-classification",
      icon: Layers,
    },
    { label: t("user_manual"), path: "http://tarhplatform.com/assets/%D9%84%D8%AF%D9%84%D9%8A%D9%84%20%D8%A7%D9%84%D8%AA%D8%B9%D8%B1%D9%8A%D9%81%D9%8A%20%D9%84%D9%85%D9%86%D8%B5%D8%A9%20%D8%B7%D8%B1%D8%AD.pdf", download: true, icon: BookOpen },
    { label: t("contactUs"), path: publicLink("/contact"), icon: Headphones },
  ];

  const filteredAccountMenu = accountMenu.filter((menu) => {
    if (!subscribed) {
      return menu.path === publicLink("/contact");
    }
    if (
      location.pathname === "/launch" &&
      (menu.path === "/dashboard" || menu.path === "/notifications")
    ) {
      return false;
    }
    if (menu.path === "/notifications" && user && !documentVerified) {
      return false;
    }

    if (
      menu.path === "/delegates/permissions" &&
      (isAuthorizedOnly || !documentVerified)
    ) {
      return false;
    }

    if (
      menu.path === "/field-of-work-classification" &&
      (isAuthorizedOnly || !subscribed)
    ) {
      return false;
    }
    return true;
  });

  const logoTarget = user
    ? isRestrictedApplicant
      ? "/registration/status"
      : "/tenders"
    : "/";
  // Signed-in users with app access get the services menu (hamburger).
  const showServicesMenu = !!user && !otpPendingActive && !isRestrictedApplicant;
  const activeModuleName = activeModule
    ? getServiceModuleName(activeModule, currentLang, "dashboard", t)
    : "";

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen((prev) => (prev ? false : prev));
        // setIsMobileDropdownOpen(false);
        // setIsMobileMenuOpen(false);
        // setMoreOpen(false);
      }
    }
    function handleEscape(event) {
      if (event.key === "Escape") setIsDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [dropdownRef]);

  // On route changes, lightly re-check user profile so that
  // backend changes to document_verified (e.g. from true -> false)
  // are picked up without requiring a full page refresh.
  useEffect(() => {
    if (!user) return;
    if (user.document_verified === false) return;
    dispatch(fetchUser());
  }, [location.pathname, user?.uuid, dispatch]);

  // Toggle dropdown visibility
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const linkClasses = (path) =>
    `transition-colors duration-200 font-bold
   px-0.5 py-1 mx-0.5 text-sm
   lg:px-1 lg:py-1.5 lg:mx-1 lg:text-base whitespace-nowrap
   ${path === activeMenuPath
      ? "text-green-700 dark:text-green-700 border-b-2 border-green-700"
      : "text-gray-700 dark:text-white hover:text-green-700 hover:border-green-700 border-b-2 border-transparent"
    }`;

  const getDisabledTitle = (reason) => {
    if (reason === "soon") return t("soon");
    return t("action_not_allowed") || "Action not allowed";
  };

  const shouldMeasureDesktopOverflow = navbarMenus.length > 0;

  // The active link always stays in the row; the others fill in order until space runs out.
  const activeMenuIndex = navbarMenus.findIndex(isMenuActive);
  const desktopVisibleMenus = [];
  const desktopOverflowMenus = [];
  let shownBesideActive = 0;
  navbarMenus.forEach((item, idx) => {
    if (idx === activeMenuIndex) {
      desktopVisibleMenus.push(item);
    } else if (shownBesideActive < desktopVisibleMenuCount) {
      desktopVisibleMenus.push(item);
      shownBesideActive += 1;
    } else {
      desktopOverflowMenus.push(item);
    }
  });
  const desktopOverflowEntries = React.useMemo(() => {
    const out = [];
    for (const item of desktopOverflowMenus) {
      if (Array.isArray(item?.children) && item.children.length > 0) {
        out.push(...item.children);
        continue;
      }
      out.push(item);
    }
    return out;
  }, [desktopOverflowMenus]);

  React.useLayoutEffect(() => {
    if (!shouldMeasureDesktopOverflow) {
      return;
    }

    const viewport = desktopMenuViewportRef.current;
    if (!viewport) return;

    const recalc = () => {
      const el = desktopMenuViewportRef.current;
      if (!el) return;
      const containerWidth = el.clientWidth || 0;
      if (containerWidth <= 0) return;

      desktopMenuMeasureItemRefs.current = desktopMenuMeasureItemRefs.current.slice(
        0,
        navbarMenus.length
      );

      const measureNode = (node) => {
        if (!node) return 0;
        const rect = node.getBoundingClientRect();
        const style = window.getComputedStyle(node);
        const ml = parseFloat(style.marginLeft) || 0;
        const mr = parseFloat(style.marginRight) || 0;
        return (rect.width || 0) + ml + mr;
      };

      const widths = navbarMenus.map((_, idx) => {
        const node = desktopMenuMeasureItemRefs.current[idx];
        return measureNode(node);
      });

      const total = widths.reduce(
        (sum, w) => sum + (Number.isFinite(w) ? w : 0),
        0
      );
      if (total <= containerWidth) {
        setDesktopVisibleMenuCount((prev) =>
          prev === widths.length ? prev : widths.length
        );
        return;
      }

      const moreW = measureNode(desktopMenuMeasureMoreRef.current) || 44;

      // Buffer for margins/gaps so we never wrap into the logo/right area.
      const buffer = 20;
      const activeWidth = activeMenuIndex >= 0 ? widths[activeMenuIndex] || 0 : 0;
      const available = Math.max(0, containerWidth - moreW - buffer - activeWidth);

      let used = 0;
      let count = 0;
      for (let i = 0; i < widths.length; i++) {
        if (i === activeMenuIndex) continue;
        const w = widths[i] || 0;
        if (used + w <= available) {
          used += w;
          count += 1;
          continue;
        }
        break;
      }

      setDesktopVisibleMenuCount((prev) => (prev === count ? prev : count));
    };

    // Sync recalculation before paint so we never render a wrapped/overflowing row.
    recalc();

    let raf = 0;
    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recalc);
    };

    const ro = new ResizeObserver(schedule);
    ro.observe(viewport);
    window.addEventListener("resize", schedule);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [activeMenuIndex, currentLang, navbarMenus, location.pathname, shouldMeasureDesktopOverflow, user?.uuid]);

  return (
    <>
      <span className="sr-only" aria-live="polite">
        {navAnnouncement}
      </span>

      {/* Desktop Navigation */}
      <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-50 dark:bg-slate-800 dark:text-white hidden lg:block">
        <div className="mx-auto flex items-center justify-between gap-4 px-6 py-3">
          {/* Start - services menu + logo */}
          <div className="flex items-center gap-2 shrink-0">
            {showServicesMenu && (
              <DrawerToggleButton
                open={isServicesMenuOpen}
                onClick={() => setIsServicesMenuOpen((prev) => !prev)}
                label={t("services_home_label")}
                controls="services-menu"
                className="-ms-2"
              />
            )}
            <Link to={logoTarget} className="flex items-center">
              <img
                src="/logo.png"
                alt="Tarh Logo"
                className="h-10 w-auto max-w-[120px]"
              />
            </Link>
          </div>

          {/* Center - Navigation */}
          <div
            className={`flex flex-1 min-w-0 items-center justify-center ${isRtl ? "pr-2" : "pl-2"
              }`}
          >
            <div
              ref={desktopMenuViewportRef}
              className="relative w-full max-w-full overflow-visible px-4"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {/* Measure strip (offscreen) to compute overflow -> visible vs. "More" */}
              <div
                className="absolute left-[-10000px] top-[-10000px] flex min-w-max items-center justify-center pointer-events-none opacity-0"
                aria-hidden="true"
                key={`desktop-menu-measure-${currentLang}`}
              >
                {navbarMenus.map((item, idx) =>
                  item.disabled ? (
                    <span
                      key={`measure-${item.path}-${idx}`}
                      ref={(node) => {
                        desktopMenuMeasureItemRefs.current[idx] = node;
                      }}
                      className={`${linkClasses(
                        item.path
                      )} cursor-not-allowed opacity-70 relative inline-flex items-center`}
                    >
                      <span className="pr-4">{item.label}</span>
                    </span>
                  ) : Array.isArray(item.children) ? (
                    <span
                      key={`measure-${item.path}-${idx}`}
                      ref={(node) => {
                        desktopMenuMeasureItemRefs.current[idx] = node;
                      }}
                      className="group transition-colors duration-200 font-bold px-0.5 py-1 mx-0.5 text-sm lg:px-1 lg:py-1.5 lg:mx-1 lg:text-base whitespace-nowrap text-gray-700 dark:text-white border-b-2 border-transparent inline-flex items-center gap-1"
                    >
                      <span>{item.label}</span>
                      <ChevronDown size={16} />
                    </span>
                  ) : (
                    <span
                      key={`measure-${item.path}-${idx}`}
                      ref={(node) => {
                        desktopMenuMeasureItemRefs.current[idx] = node;
                      }}
                      className={linkClasses(item.path)}
                    >
                      {item.label}
                    </span>
                  )
                )}
                <span
                  ref={desktopMenuMeasureMoreRef}
                  className="group transition-colors duration-200 font-bold px-2 py-1 mx-0.5 text-sm lg:px-2 lg:py-1.5 lg:mx-1 lg:text-base whitespace-nowrap text-gray-700 dark:text-white border-b-2 border-transparent inline-flex items-center gap-1"
                >
                  <MoreVertical size={18} />
                </span>
              </div>

              <div
                key={`desktop-menu-row-${currentLang}-${navModuleKey}`}
                className={`mx-auto flex min-w-0 items-center justify-center gap-x-0 [&::-webkit-scrollbar]:hidden ${navSwap.animate ? "animate-nav-in motion-reduce:animate-none" : ""
                  }`}
              >
                {desktopVisibleMenus.map((item, idx) =>
                  item.disabled ? (
                    <span
                      key={`menu-${idx}-${item.path}`}
                      className={`${linkClasses(
                        item.path
                      )} cursor-not-allowed opacity-70 relative inline-flex items-center`}
                      title={getDisabledTitle(item.disabledReason)}
                    >
                      <span className="pr-4">
                        {item.label}{" "}
                        {item.disabledReason === "soon" && (
                          <span
                            className={`absolute -top-2 text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-300 uppercase tracking-wide ${isRtl ? "left-0" : "right-0"
                              }`}
                          >
                            {t("soon")}
                          </span>
                        )}
                      </span>
                    </span>
                  ) : Array.isArray(item.children) ? (
                    <NavMenu
                      key={`menu-${idx}-${item.path}`}
                      open={openMenuKey === item.path}
                      onOpen={() => openMenu(item.path)}
                      onToggle={() => toggleMenu(item.path)}
                      onClose={() => setOpenMenuKey(null)}
                      onCloseSoon={closeMenuSoon}
                      isRtl={isRtl}
                      buttonClassName={`group transition-colors duration-200 font-bold px-0.5 py-1 mx-0.5 text-sm lg:px-1 lg:py-1.5 lg:mx-1 lg:text-base whitespace-nowrap hover:text-green-700 hover:border-green-700 border-b-2 inline-flex items-center gap-1 ${isMenuActive(item)
                        ? "text-green-700 border-green-700"
                        : "text-gray-700 dark:text-white border-transparent"
                        }`}
                      items={item.children.map(withLinkState)}
                      activePath={activeMenuPath}
                      getDisabledTitle={getDisabledTitle}
                      soonLabel={t("soon")}
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        size={16}
                        className={`opacity-0 group-hover:opacity-100 transition-all duration-200 ${openMenuKey === item.path ? "rotate-180 opacity-100" : ""
                          }`}
                      />
                    </NavMenu>
                  ) : (
                    <Link
                      key={`menu-${idx}-${item.path}`}
                      to={item.path}
                      state={withLinkState(item).state}
                      aria-current={isMenuActive(item) ? "page" : undefined}
                      className={linkClasses(item.path)}
                    >
                      {item.label}
                    </Link>
                  )
                )}
                {desktopOverflowEntries.length > 0 && (
                  <NavMenu
                    open={openMenuKey === "__more_overflow__"}
                    onOpen={() => openMenu("__more_overflow__")}
                    onToggle={() => toggleMenu("__more_overflow__")}
                    onClose={() => setOpenMenuKey(null)}
                    onCloseSoon={closeMenuSoon}
                    isRtl={isRtl}
                    buttonClassName="group transition-colors duration-200 font-bold px-2 py-1 mx-0.5 text-sm lg:px-2 lg:py-1.5 lg:mx-1 lg:text-base whitespace-nowrap text-gray-700 dark:text-white hover:text-green-700 hover:border-green-700 border-b-2 border-transparent inline-flex items-center"
                    buttonLabel={t("more") || "More"}
                    items={desktopOverflowEntries.map(withLinkState)}
                    activePath={activeMenuPath}
                    getDisabledTitle={getDisabledTitle}
                    soonLabel={t("soon")}
                  >
                    <MoreVertical size={18} />
                  </NavMenu>
                )}
              </div>
            </div>
          </div>

          {/* Right - Lang + Auth */}
          <div className="flex flex-nowrap items-center gap-x-1 shrink-0">
            {showBranchSwitcher && (
              <select
                aria-label={t("active_branch")}
                value={activeBranchId}
                onChange={handleBranchChange}
                className="max-w-44 rounded-lg border border-gray-200 bg-white px-2 py-2 text-xs font-semibold text-gray-700 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
              >
                {branchAssignments.map((assignment) => (
                  <option key={assignment.id || assignment.branch_id} value={assignment.branch_public_id || assignment.branch_id}>
                    {assignment.branch_name}
                  </option>
                ))}
              </select>
            )}
            {/* Language Switch */}
            <LanguageSwitcher />

            {/* <ThemeSwitcher /> */}

            {user ? (
              <>
                {documentVerified && walletCompanyId && (
                  <Link
                    to="/profile?tab=points"
                    title={walletPointsLabel}
                    aria-label={walletPointsLabel}
                    className="group inline-flex h-9 items-center gap-1.5 rounded-full border border-slate-200 bg-white ps-1 pe-2.5 text-[13px] transition-colors duration-200 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--primary-green)/40 dark:border-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700"
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-linear-to-br from-emerald-400 to-(--primary-green) text-white shadow-sm ring-1 ring-emerald-700/20 transition-transform duration-300 group-hover:rotate-12"
                    >
                      <Coins size={14} strokeWidth={2.25} />
                    </span>
                    {walletPoints === null ? (
                      // Placeholder while the balance loads, instead of a dash.
                      <span aria-hidden="true" className="h-3 w-9 animate-pulse rounded bg-slate-200 dark:bg-slate-600" />
                    ) : (
                      <bdi dir="ltr" className="font-bold tabular-nums text-slate-900 dark:text-white">
                        {formattedWalletPoints}
                      </bdi>
                    )}
                    <span className="hidden text-xs font-medium text-slate-500 sm:inline dark:text-slate-400">
                      {t("wallet_points_unit")}
                    </span>
                  </Link>
                )}
                {documentVerified && location.pathname !== "/launch" && (
                  <NotificationsMenu
                    open={nfDropdownOpen}
                    onOpenChange={setNfDropdownOpen}
                    unread={unread}
                    unreadCount={unreadCount}
                    companyId={user?.company_id || user?.company?.id || null}
                    onMarkAsRead={handlemarkAsRead}
                  />
                )}

                <div className="relative ms-1" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen((prev) => !prev)}
                    aria-haspopup="menu"
                    aria-expanded={isDropdownOpen}
                    aria-label={t("profile")}
                    className={`group flex items-center gap-1.5 rounded-full p-1 pe-2 transition-colors duration-200 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--primary-green)/40 dark:hover:bg-slate-700 ${isDropdownOpen ? "bg-slate-100 dark:bg-slate-700" : ""}`}
                  >
                    <CompanyAvatar src={user.avatar_url} alt={user?.name || ""} />
                    <ChevronDown
                      size={16}
                      strokeWidth={2.25}
                      aria-hidden="true"
                      className={`transition-transform duration-200 ${isDropdownOpen ? "rotate-180 text-(--primary-green)" : "text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200"}`}
                    />
                  </button>

                  {isDropdownOpen && (
                    <div
                      role="menu"
                      className="absolute end-0 z-20 mt-2 w-64 origin-top-right rtl:origin-top-left animate-menu-in rounded-2xl border border-slate-200 bg-white p-1.5 shadow-xl shadow-slate-900/10 motion-reduce:animate-none dark:border-slate-700 dark:bg-slate-800"
                    >
                      <div className="px-2.5 py-2.5">
                        <AccountMenuHeader user={user} />
                      </div>
                      <AccountMenuDivider />
                      <AccountMenuItems
                        items={filteredAccountMenu}
                        pathname={location.pathname}
                        itemRole="menuitem"
                        onNavigate={() => setIsDropdownOpen(false)}
                        onLogout={() => {
                          dispatch(logout());
                          setIsDropdownOpen(false);
                        }}
                        logoutLabel={t("logout")}
                      />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                {/* <Link
                  to="/register"
                  className="px-5 py-2 rounded-full border border-green-700 text-green-700 hover:bg-green-50 transition"
                >
                  {t("register")}
                </Link> */}
                <Link
                  to="/login"
                  className="flex items-center space-x-2 px-5 py-2 rounded bg-green-700 text-white hover:bg-green-800 transition"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{t("login")}</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Desktop services menu. Its header repeats the toggle and logo in the same spot,
          so the icon closes it again; overlay click and Escape close it too. */}
      {showServicesMenu && (
        <SideDrawer
          open={isServicesMenuOpen}
          onClose={() => setIsServicesMenuOpen(false)}
          labelledBy="services-menu-title"
          zIndex="z-[60]"
          className="hidden lg:block"
        >
          <div id="services-menu" className="flex h-full flex-col">
            {/* Same box as the navbar row (its tallest item, the account button, is 44px) and the
                border sits outside it, so the toggle and logo don't shift when the menu opens. */}
            <div className="box-content flex h-11 items-center gap-2 border-b border-slate-100 px-6 py-3 dark:border-slate-700">
              <DrawerToggleButton
                open
                data-autofocus
                onClick={() => setIsServicesMenuOpen(false)}
                label={t("close")}
                className="-ms-2"
              />
              <Link to={logoTarget} onClick={() => setIsServicesMenuOpen(false)} className="flex items-center">
                <img src="/logo.png" alt="Tarh Logo" className="h-10 w-auto max-w-[120px]" />
              </Link>
              <span id="services-menu-title" className="sr-only">
                {t("services_main_dashboard_title")}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-3">
              <ServicesNavList
                activeKey={activeModule?.key}
                onNavigate={() => setIsServicesMenuOpen(false)}
              />
            </div>
          </div>
        </SideDrawer>
      )}

      {/* Mobile Navigation */}
      <nav className="lg:hidden bg-white dark:bg-slate-800 dark:text-white">
          <div
            className="fixed top-0 py-3 px-5 w-full flex justify-between items-center bg-white shadow-lg z-40 pt-0 mt-0 bg-white dark:bg-slate-800 dark:text-white"
            style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.75rem)" }}
          >
            <Link to={logoTarget} aria-label="logo" className="flex items-center">
              <img src="/logo.png" alt="Tarh Logo" className="h-15 w-auto" />
            </Link>
            <div className="flex items-center gap-3">
              {showBranchSwitcher && (
                <select
                  aria-label={t("active_branch")}
                  value={activeBranchId}
                  onChange={handleBranchChange}
                  className="max-w-32 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-700 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                >
                  {branchAssignments.map((assignment) => (
                    <option key={assignment.id || assignment.branch_id} value={assignment.branch_public_id || assignment.branch_id}>
                      {assignment.branch_name}
                    </option>
                  ))}
                </select>
              )}
              {/* Language Switcher Mobile */}
              <LanguageSwitcher />

              {user && documentVerified && location.pathname !== "/launch" && (
                <NotificationsMenu
                  variant="mobile"
                  open={nfDropdownOpen}
                  onOpenChange={setNfDropdownOpen}
                  unread={unread}
                  unreadCount={unreadCount}
                  companyId={user?.company_id || user?.company?.id || null}
                  onMarkAsRead={handlemarkAsRead}
                />
              )}

              <DrawerToggleButton
                id="menu"
                open={isMobileMenuOpen}
                onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                label={t("services_home_label")}
                controls="mobile-menu"
                className="-me-2"
              />
            </div>
          </div>
        {/* Mobile & tablet sidebar */}
          <SideDrawer
            open={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
            labelledBy="mobile-menu-title"
            className="lg:hidden"
          >
              <span id="mobile-menu-title" className="sr-only">
                {t("services_home_label")}
              </span>
              <div id="mobile-menu" className="flex flex-1 flex-col overflow-y-auto overscroll-contain px-4">
                <div>
                  {/* Header */}
                  <div className="mt-5 flex w-full items-center justify-between border-b border-slate-100 pb-4 dark:border-slate-700">
                    <Link to={logoTarget} onClick={() => setIsMobileMenuOpen(false)}>
                      <img
                        src="/logo.png"
                        alt="Tarh Logo"
                        className="h-15 w-auto"
                      />
                    </Link>
                    <button
                      id="cross"
                      type="button"
                      data-autofocus
                      aria-label={t("close")}
                      className="group -me-1 flex h-11 w-11 items-center justify-center rounded-full focus:outline-none"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {/* 36px visible circle inside a 44px tap area (Apple HIG minimum). */}
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition-all duration-200 group-hover:bg-slate-200 group-hover:text-slate-900 group-active:scale-95 group-focus-visible:ring-2 group-focus-visible:ring-(--primary-green)/40 dark:bg-slate-700 dark:text-slate-200 dark:group-hover:bg-slate-600 dark:group-hover:text-white">
                        <X size={18} strokeWidth={2.25} aria-hidden="true" className="transition-transform duration-300 group-hover:rotate-90" />
                      </span>
                    </button>
                  </div>

                  {/* All services */}
                  {showServicesMenu && (
                    <div className="mt-3 border-b border-slate-100 pb-3 dark:border-slate-700">
                      <ServicesNavList
                        size="touch"
                        activeKey={activeModule?.key}
                        onNavigate={() => setIsMobileMenuOpen(false)}
                      />
                    </div>
                  )}

                  {/* Current service's pages (or public site links when signed out) */}
                  {isModuleNavbarMode && activeModuleName && (
                    <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      {activeModuleName}
                    </p>
                  )}
                  <ul className={`${isModuleNavbarMode && activeModuleName ? "mt-0.5" : "mt-3"} space-y-0.5`}>
                    {navbarMenus.map((item, idx) => (
                      <li key={`mobile-menu-${idx}-${item.path}`}>
                        {item.disabled ? (
                          <span
                            className={drawerDisabledClass()}
                            title={getDisabledTitle(item.disabledReason)}
                          >
                            {item.label}
                            {item.disabledReason === "soon" && (
                              <span className={SOON_BADGE_CLASS}>{t("soon")}</span>
                            )}
                          </span>
                        ) : Array.isArray(item.children) ? (
                          <div>
                            <button
                              type="button"
                              aria-expanded={mobileOpenMenuKey === item.path}
                              className={drawerRowClass(
                                (item.children || []).some((child) => child.path === activeMenuPath)
                              )}
                              onClick={() =>
                                setMobileOpenMenuKey((prev) =>
                                  prev === item.path ? null : item.path
                                )
                              }
                            >
                              {item.label}
                              <ChevronDown
                                size={18}
                                strokeWidth={2.25}
                                aria-hidden="true"
                                className={`ms-auto shrink-0 transition-transform duration-200 ${
                                  mobileOpenMenuKey === item.path ? "rotate-180 text-(--primary-green)" : "text-slate-400"
                                }`}
                              />
                            </button>
                            {mobileOpenMenuKey === item.path && (
                              <div className="ms-4 mt-0.5 mb-1 animate-menu-in space-y-0.5 border-s border-slate-200 ps-2 motion-reduce:animate-none dark:border-slate-700">
                                {(item.children || []).map((child) =>
                                  child.disabled ? (
                                    <span
                                      key={child.path}
                                      className={drawerDisabledClass("sm")}
                                      title={getDisabledTitle(child.disabledReason)}
                                    >
                                      {child.label}
                                      {child.disabledReason === "soon" && (
                                        <span className={SOON_BADGE_CLASS}>{t("soon")}</span>
                                      )}
                                    </span>
                                  ) : (
                                    <Link
                                      key={child.path}
                                      to={child.path}
                                      state={withLinkState(child).state}
                                      aria-current={child.path === activeMenuPath ? "page" : undefined}
                                      className={drawerRowClass(child.path === activeMenuPath, "sm")}
                                      onClick={() => {
                                        setMobileOpenMenuKey(null);
                                        setIsMobileMenuOpen(false);
                                      }}
                                    >
                                      {child.label}
                                    </Link>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Link
                            to={item.path}
                            state={withLinkState(item).state}
                            aria-current={item.path === activeMenuPath ? "page" : undefined}
                            className={drawerRowClass(item.path === activeMenuPath)}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {item.label}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Footer */}
                <div className="mt-auto border-t border-slate-100 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] dark:border-slate-700">
                  {user ? (
                    <div className="flex flex-col w-full dark:bg-slate-800 dark:text-white">
                      {/* Options open above the profile row so the row stays pinned to the bottom. */}
                      {isMobileDropdownOpen && (
                        <div className="mb-2 flex origin-bottom flex-col animate-nav-in rounded-xl border border-slate-200 bg-white p-1.5 motion-reduce:animate-none dark:border-slate-600 dark:bg-slate-800">
                          <AccountMenuItems
                            items={filteredAccountMenu}
                            pathname={location.pathname}
                            size="touch"
                            onNavigate={() => {
                              setIsMobileDropdownOpen(false);
                              setIsMobileMenuOpen(false);
                            }}
                            onLogout={() => {
                              dispatch(logout());
                              setIsMobileMenuOpen(false);
                              setIsMobileDropdownOpen(false);
                            }}
                            logoutLabel={t("logout")}
                          />
                        </div>
                      )}

                      {/* Profile row: tap to show the account options */}
                      <button
                        type="button"
                        onClick={() => setIsMobileDropdownOpen((prev) => !prev)}
                        aria-expanded={isMobileDropdownOpen}
                        className={`flex w-full items-center gap-2 rounded-xl p-2 transition-colors duration-200 active:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-(--primary-green)/40 dark:active:bg-slate-700 ${isMobileDropdownOpen ? "bg-slate-50 dark:bg-slate-700/60" : ""}`}
                      >
                        <AccountMenuHeader user={user} />
                        {/* Points up toward the options; flips down once they're open. */}
                        <ChevronDown
                          size={18}
                          strokeWidth={2.25}
                          aria-hidden="true"
                          className={`shrink-0 transition-transform duration-200 ${isMobileDropdownOpen ? "text-(--primary-green)" : "rotate-180 text-slate-400 dark:text-slate-300"}`}
                        />
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      className="flex items-center space-x-2 text-white font-medium py-2 px-6 rounded transition-colors duration-200 w-full justify-center "
                      style={{ backgroundColor: "#1B8354" }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>{t("login")}</span>
                    </Link>
                  )}
                </div>
              </div>
          </SideDrawer>
      </nav>
    </>
  );
}
